const PROTECTED = /```[\s\S]*?```|`[^`\n]*`/g;
const SENTINEL_OPEN = '​__TBL_PROT_';
const SENTINEL_CLOSE = '_PROT_TBL__​';
const SENTINEL_PATTERN = /​__TBL_PROT_(\d+)_PROT_TBL__​/g;

const SEP_CELL = /^\s*:?-+:?\s*$/;
const SEP_ALLOWED = /^[\s\-:|]+$/;

function splitCells(line: string): string[] {
  // 양끝 파이프 제거 후 split. `\|` 이스케이프는 GFM 표 본문에서 드물어 미처리.
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|');
}

function isHeaderCandidate(line: string): boolean {
  const t = line.trim();
  if (!t.startsWith('|') || !t.endsWith('|')) return false;
  return splitCells(t).length >= 2;
}

function isSeparatorRow(line: string): boolean {
  const t = line.trim();
  if (!t.startsWith('|') || !t.endsWith('|')) return false;
  if (!t.includes('-')) return false;
  if (!SEP_ALLOWED.test(t)) return false;
  const cells = splitCells(t);
  if (cells.length === 0) return false;
  return cells.every((c) => SEP_CELL.test(c));
}

function buildSeparator(existingCells: string[], targetCount: number): string {
  const out: string[] = [];
  for (let i = 0; i < targetCount; i++) {
    const src = existingCells[i]?.trim();
    if (src && SEP_CELL.test(src)) {
      out.push(` ${src} `);
    } else {
      out.push(' :--- ');
    }
  }
  return `|${out.join('|')}|`;
}

/**
 * LLM (특히 gemma 계열) 이 마크다운 표를 출력할 때 헤더와 구분자 행의 셀 수가
 * 불일치하는 경우가 있다. GFM 명세상 셀 수가 다르면 표로 파싱되지 않아 본문이
 * 텍스트 덤프로 떨어지므로, 클라이언트 렌더 직전에 구분자 행을 헤더에 맞춰
 * 패딩 / 트림한다. 정렬 마커 (`:---`, `:---:`, `---:`) 는 가능한 한 보존.
 *
 * 코드 펜스 / 인라인 코드 안의 `|...|` 는 표가 아니므로 정규화에서 제외한다.
 */
export function normalizeMarkdownTables(text: string): string {
  if (!text || !text.includes('|')) return text;

  const stash: string[] = [];
  const masked = text.replace(PROTECTED, (m) => {
    stash.push(m);
    return `${SENTINEL_OPEN}${stash.length - 1}${SENTINEL_CLOSE}`;
  });

  const lines = masked.split('\n');
  for (let i = 0; i < lines.length - 1; i++) {
    const header = lines[i];
    const sep = lines[i + 1];
    if (header === undefined || sep === undefined) continue;
    if (!isHeaderCandidate(header)) continue;
    if (!isSeparatorRow(sep)) continue;
    const headerCount = splitCells(header).length;
    const sepCells = splitCells(sep);
    if (sepCells.length === headerCount) continue;
    lines[i + 1] = buildSeparator(sepCells, headerCount);
  }

  return lines.join('\n').replace(SENTINEL_PATTERN, (_, idx: string) => stash[Number(idx)] ?? '');
}
